import { apiClient } from '../lib/apiClient';

/** Public legal documents served by `GET /api/legal/:id` (src/routes/legal.ts). */
export type LegalDocumentId = 'privacy-policy' | 'terms-of-service' | 'imprint';
export type LegalLanguage = 'en' | 'de';

export interface LegalDocument {
  id: LegalDocumentId;
  lang: LegalLanguage;
  title: string;
  /** null for the imprint. */
  version: string | null;
  effectiveDate: string | null;
  /** Markdown body without the title heading. */
  content: string;
  operatorDetailsComplete: boolean;
  availableLanguages: LegalLanguage[];
}

/** The documents exist in English and German; every other app locale reads English. */
export function legalLanguageFor(appLanguage: string | undefined): LegalLanguage {
  return (appLanguage ?? '').toLowerCase().startsWith('de') ? 'de' : 'en';
}

export async function fetchLegalDocument(
  id: LegalDocumentId,
  lang: LegalLanguage,
  signal?: AbortSignal,
): Promise<LegalDocument> {
  const res = await apiClient.get<{ data: LegalDocument }>(`/api/legal/${id}?lang=${lang}`, { signal });
  return res.data;
}

/** Formats an ISO date (YYYY-MM-DD) for the UI locale without timezone drift. */
export function formatLegalDate(iso: string, locale: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(date);
  } catch {
    return iso;
  }
}
